import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIconModule } from '@angular/material/icon';
import { Subscription, catchError, of, startWith, timer } from 'rxjs';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { StatGroupComponent, StatItem } from '../../components/stat-group/stat-group.component';
import { ResetFilterButtonComponent } from '../../components/reset-filter-button/reset-filter-button.component';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { AvatarComponent } from '../../components/avatar/avatar.component';
import { SavedViewOption, SavedViewsMenuComponent } from '../../shared/saved-views/saved-views-menu.component';
import { DefectService } from '../../services/defect.service';
import { ReleaseService } from '../../services/release.service';
import { ProjectMemberService } from '../../services/project-member.service';
import { SavedViewService } from '../../services/saved-view.service';
import { DefectListFilters, DefectListItem, DefectStatus, DefectSummary } from '../../models/defect.model';
import { Release } from '../../models/release.model';
import { ProjectMemberDTO } from '../../models/project-member.model';
import { SavedView } from '../../models/saved-view.model';
import { reviewerOptionsFromMembers } from '../../shared/project-member-options';
import { DEFECT_STATUSES, defectSeverityTone, defectStatusTone } from './defect-status';
import {
  DefectSavedViewFilters,
  DefectSavedViewLabels,
  UNASSIGNED_FILTER,
  buildDefectSavedViewFiltersJson,
  defectPageFiltersFromView,
  defectViewFiltersFromPage,
  isDefectSavedViewDirty,
  knownDefectSavedViewFilters,
  parseDefectSavedViewFilters,
  summarizeDefectSavedView,
} from './defect-saved-views';

const PAGE_SIZE_OPTIONS: readonly number[] = [5, 10, 25];
const QUERY_DEBOUNCE_MS = 200;

interface SelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-defects-page',
  imports: [
    TranslatePipe,
    MatIconModule,
    ProjectNavComponent,
    PageHeaderComponent,
    StatGroupComponent,
    ResetFilterButtonComponent,
    SearchBarComponent,
    AvatarComponent,
    SavedViewsMenuComponent,
  ],
  templateUrl: './defects-page.component.html',
  styleUrl: './defects-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DefectsPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly defectService = inject(DefectService);
  private readonly releaseService = inject(ReleaseService);
  private readonly memberService = inject(ProjectMemberService);
  private readonly savedViewService = inject(SavedViewService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private request?: Subscription;
  private pendingQuery?: Subscription;

  readonly projectId: string = this.route.snapshot.params['projectId'];
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly severityTone = defectSeverityTone;
  readonly statusTone = defectStatusTone;

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly defects = signal<DefectListItem[]>([]);
  readonly totalElements = signal(0);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(10);
  readonly summary = signal<DefectSummary | null>(null);

  readonly query = signal('');
  readonly statusFilter = signal('');
  readonly releaseFilter = signal('');
  readonly assigneeFilter = signal('');

  readonly savedViews = signal<SavedView[]>([]);
  readonly activeViewName = signal<string | null>(null);
  readonly viewsErrorKey = signal<string | null>(null);

  private readonly releases = signal<Release[]>([]);
  private readonly members = signal<ProjectMemberDTO[]>([]);
  private readonly people = computed(() => reviewerOptionsFromMembers(this.members()));

  readonly hasFilter = computed(
    () => !!(this.query().trim() || this.statusFilter() || this.releaseFilter() || this.assigneeFilter()),
  );
  readonly hasRows = computed(() => !this.loading() && !this.loadError() && this.defects().length > 0);
  readonly pageCount = computed(() => Math.max(1, Math.ceil(this.totalElements() / this.pageSize())));
  readonly rangeStart = computed(() => this.pageIndex() * this.pageSize() + 1);
  readonly rangeEnd = computed(() => Math.min((this.pageIndex() + 1) * this.pageSize(), this.totalElements()));

  private readonly langChange = toSignal(this.translate.onLangChange.pipe(startWith(null)), { initialValue: null });

  readonly statusOptions = computed<SelectOption[]>(() => {
    this.langChange();
    return DEFECT_STATUSES.map((status) => ({
      value: status,
      label: this.translate.instant(`defect.status.${status}`),
    }));
  });

  readonly releaseOptions = computed<SelectOption[]>(() => {
    this.langChange();
    return this.releases().map((release) => ({
      value: release.id,
      label: this.translate.instant('defect.list.filter.releaseOption', { id: release.id }),
    }));
  });

  readonly assigneeOptions = computed<SelectOption[]>(() => {
    this.langChange();
    return [
      { value: UNASSIGNED_FILTER, label: this.translate.instant('defect.list.unassigned') },
      ...this.people().map((person) => ({ value: person.id, label: person.displayName })),
    ];
  });

  private readonly knownFilterIds = computed(() => ({
    releaseIds: this.releases().map((release) => release.id),
    assigneeIds: this.people().map((person) => person.id),
  }));

  private readonly currentViewFilters = computed(() =>
    defectViewFiltersFromPage({
      status: this.statusFilter(),
      release: this.releaseFilter(),
      assignee: this.assigneeFilter(),
    }),
  );

  readonly savedViewOptions = computed<SavedViewOption[]>(() => {
    this.langChange();
    const labels: DefectSavedViewLabels = {
      status: (status) => this.translate.instant(`defect.status.${status}`),
      release: (id) => this.translate.instant('defect.list.filter.releaseOption', { id }),
      person: (id) => this.people().find((person) => person.id === id)?.displayName ?? null,
      unassigned: this.translate.instant('defect.list.unassigned'),
      all: this.translate.instant('defect.list.views.summaryAll'),
    };
    return this.savedViews().map((view) => ({
      name: view.name,
      summary: summarizeDefectSavedView(this.storedFilters(view), labels),
    }));
  });

  /** Nur die drei Auswahlen zählen; die Suche gehört nicht zur Ansicht. */
  readonly activeViewDirty = computed(() => {
    const view = this.savedViews().find((candidate) => candidate.name === this.activeViewName());
    return !!view && isDefectSavedViewDirty(this.storedFilters(view), this.currentViewFilters());
  });

  readonly stats = computed((): StatItem[] => {
    this.langChange();
    const summary = this.summary();
    return [
      { label: this.translate.instant('defect.list.stats.total'), value: summary?.total ?? '–' },
      {
        label: this.translate.instant('defect.list.stats.open'),
        value: summary?.open ?? '–',
        color: 'var(--to-brand)',
      },
      {
        label: this.translate.instant('defect.list.stats.blocking'),
        value: summary?.blocking ?? '–',
        color: 'var(--to-red-fg)',
      },
    ];
  });

  ngOnInit(): void {
    this.releaseService
      .list(this.projectId)
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((releases) => this.releases.set(releases));
    this.memberService
      .getMembers(this.projectId)
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((members) => this.members.set(members));
    this.defectService
      .summary(this.projectId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((summary) => this.summary.set(summary));
    this.savedViewService
      .list(this.projectId, 'DEFECTS')
      .pipe(
        catchError(() => of([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((views) => this.savedViews.set(views));
    this.load();
  }

  load(): void {
    this.pendingQuery?.unsubscribe();
    this.request?.unsubscribe();
    this.loading.set(true);
    this.loadError.set(false);
    this.request = this.defectService
      .list(this.projectId, this.filters())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          this.defects.set(page.content);
          this.totalElements.set(page.totalElements);
          this.loading.set(false);
        },
        error: () => {
          this.defects.set([]);
          this.totalElements.set(0);
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  /** Tippen lädt erst nach der Ruhephase, genau einmal; jeder andere Ladevorgang verwirft die wartende Suche. */
  onQuery(value: string): void {
    this.query.set(value);
    this.pendingQuery?.unsubscribe();
    this.pendingQuery = timer(QUERY_DEBOUNCE_MS)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reloadFromStart());
  }

  onStatusFilter(value: string): void {
    this.statusFilter.set(value);
    this.reloadFromStart();
  }

  onReleaseFilter(value: string): void {
    this.releaseFilter.set(value);
    this.reloadFromStart();
  }

  onAssigneeFilter(value: string): void {
    this.assigneeFilter.set(value);
    this.reloadFromStart();
  }

  resetFilters(): void {
    this.query.set('');
    this.statusFilter.set('');
    this.releaseFilter.set('');
    this.assigneeFilter.set('');
    this.reloadFromStart();
  }

  /** Setzt nur die drei Auswahlen; die Suche bleibt stehen (page-fehler.jsx:134). */
  onViewSelected(name: string): void {
    const view = this.savedViews().find((candidate) => candidate.name === name);
    if (!view) return;
    const filters = defectPageFiltersFromView(this.storedFilters(view));
    this.statusFilter.set(filters.status);
    this.releaseFilter.set(filters.release);
    this.assigneeFilter.set(filters.assignee);
    this.activeViewName.set(view.name);
    this.reloadFromStart();
  }

  /** Speichern und „aktualisieren“ schreiben beide per POST unter dem Namen der Ansicht. */
  saveView(name: string): void {
    const filtersJson = buildDefectSavedViewFiltersJson(this.currentViewFilters());
    this.savedViewService
      .upsert(this.projectId, { name, context: 'DEFECTS', filtersJson })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.savedViews.update((views) =>
            [...views.filter((view) => view.name !== saved.name), saved].sort((a, b) => a.name.localeCompare(b.name)),
          );
          this.activeViewName.set(saved.name);
          this.viewsErrorKey.set(null);
        },
        error: () => this.viewsErrorKey.set('defect.list.views.saveError'),
      });
  }

  onViewDeleted(name: string): void {
    this.savedViews.update((views) => views.filter((view) => view.name !== name));
    if (this.activeViewName() === name) this.activeViewName.set(null);
  }

  onViewDeleteFailed(): void {
    this.viewsErrorKey.set('defect.list.views.deleteError');
  }

  onPageIndex(index: number): void {
    this.pageIndex.set(index);
    this.load();
  }

  onPageSize(size: number): void {
    this.pageSize.set(size);
    this.reloadFromStart();
  }

  open(defect: DefectListItem): void {
    this.router.navigate(['/projects', this.projectId, 'defects', defect.id]);
  }

  private reloadFromStart(): void {
    this.pageIndex.set(0);
    this.load();
  }

  private storedFilters(view: SavedView): DefectSavedViewFilters {
    return knownDefectSavedViewFilters(parseDefectSavedViewFilters(view.filtersJson), this.knownFilterIds());
  }

  private filters(): DefectListFilters {
    const filters: DefectListFilters = { page: this.pageIndex(), size: this.pageSize() };
    const query = this.query().trim();
    if (query) filters.query = query;
    if (this.statusFilter()) filters.status = [this.statusFilter() as DefectStatus];
    if (this.releaseFilter()) filters.releaseId = this.releaseFilter();
    if (this.assigneeFilter() === UNASSIGNED_FILTER) filters.assignee = 'none';
    else if (this.assigneeFilter()) filters.assigneeUserId = this.assigneeFilter();
    return filters;
  }
}
